import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Gift } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const CATEGORIES = ["product", "discount", "experience", "merch"];
const EMPTY = { title: "", description: "", cost_coins: 100, image_url: "", category: "product", is_active: true, stock: null, sort_order: 0 };

export default function AdminRewards() {
  const [rewards, setRewards] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/rewards");
      setRewards(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    try {
      if (r.id) await api.put(`/admin/rewards/${r.id}`, r);
      else await api.post("/admin/rewards", r);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/admin/rewards/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-rewards-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Loyalty</div>
          <h1 className="editorial-h1 text-4xl">Rewards Catalogue</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-pill btn-dark text-xs" data-testid="new-reward-btn"><Plus size={14} /> New reward</button>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rewards.length === 0 && <div className="text-brand-charcoal/50">No rewards yet. Create your first!</div>}
        {rewards.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white border border-brand-line overflow-hidden" data-testid={`reward-admin-${r.id}`}>
            <div className="h-28 bg-brand-yellow/20 flex items-center justify-center">
              {r.image_url ? <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" /> : <Gift className="text-brand-charcoal/40" size={34} />}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">{r.category}</div>
              <div className="font-display text-lg mt-1">{r.title}</div>
              <div className="text-xs text-brand-charcoal/60 mt-1 min-h-[32px]">{r.description}</div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <div className="font-mono">{r.cost_coins} coins</div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{r.is_active ? "Active" : "Hidden"}</span>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => setEditing(r)} data-testid={`edit-reward-${r.id}`} className="text-xs text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(r)} data-testid={`delete-reward-${r.id}`} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <EditReward value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function EditReward({ value, onClose, onSave }) {
  const [f, setF] = useState(value);
  const update = (k) => (e) => {
    const v = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setF((prev) => ({ ...prev, [k]: v }));
  };
  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...f,
      cost_coins: parseInt(f.cost_coins) || 0,
      sort_order: parseInt(f.sort_order) || 0,
      stock: f.stock === "" || f.stock === null || f.stock === undefined ? null : parseInt(f.stock),
      image_url: f.image_url || null,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 overflow-auto">
      <form onSubmit={submit} data-testid="reward-edit-modal" className="w-full max-w-lg rounded-2xl bg-white p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">{f.id ? "Edit reward" : "New reward"}</div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Title</label><input required value={f.title} onChange={update("title")} className="pp-input" data-testid="reward-title-input" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Description</label><textarea value={f.description || ""} onChange={update("description")} rows={2} className="pp-input" data-testid="reward-desc-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Cost (coins)</label><input required type="number" value={f.cost_coins} onChange={update("cost_coins")} className="pp-input" data-testid="reward-cost-input" /></div>
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Sort order</label><input type="number" value={f.sort_order} onChange={update("sort_order")} className="pp-input" data-testid="reward-sort-input" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Category</label>
            <select value={f.category} onChange={update("category")} className="pp-input" data-testid="reward-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Stock (blank = unlimited)</label><input type="number" value={f.stock ?? ""} onChange={update("stock")} className="pp-input" data-testid="reward-stock-input" /></div>
        </div>
        <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Image URL</label><input value={f.image_url || ""} onChange={update("image_url")} className="pp-input" data-testid="reward-image-input" placeholder="https://..." /></div>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.is_active} onChange={update("is_active")} data-testid="reward-active-toggle" /> Active</label>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-pill btn-ghost text-xs" data-testid="reward-cancel">Cancel</button>
          <button className="btn-pill btn-dark text-xs" data-testid="reward-save">Save</button>
        </div>
      </form>
    </div>
  );
}
